/**
 * nav-theme-bridge.ts — Runtime bridge for nav CSS variables to system tokens.
 *
 * Problem:
 *   The left/top nav SCSS uses legacy --left-nav-* CSS vars set by
 *   rd-theme('dark'/'light', ...) selectors keyed on [data-theme-type].
 *   These are build-time values that can't respond to a runtime toggle.
 *
 * Solution:
 *   When the radiant flow is active, override --left-nav-* on #root with
 *   var(--rd-sys-color-*) references. This makes nav colors follow the
 *   system token layer, which automatically adapts to light/dark/brand
 *   themes via [data-theme='dark'] and applyTheme() overrides.
 *
 * When deactivated, all overrides are removed and the SCSS-compiled
 * defaults (rd-theme() values) take over — preserving legacy behavior.
 *
 * The left-nav vars always follow the system tokens. The top-nav vars have a
 * second mode: "apply as top nav color" derives them from the literal brand
 * hex, by the same luminance math the pre-radiant Custom dropdown used.
 */
import {
    prepareTopNavInternalVariables,
    TopNavInternalVariablesProps,
} from '../top-nav-v3/top-nav-v3-theme-utils';
import { normalizeBrandColor } from './apply-theme';

/**
 * Maps each legacy left-nav CSS variable to its system token counterpart.
 *
 * Left side:  CSS var set by rd-theme() in authenticated-app-layout.module.scss
 * Right side: system token name from _tokens-system.scss
 *
 * Reference (light theme resolved values):
 *   background-base      → #FFFFFF  (was rd-theme('light', 'left-nav-background'))
 *   background-on-base         → #F6F8FA  (was rd-theme('light', 'left-nav-active-tab-background'))
 *   border-subtle          → #EAEDF2  (was rd-theme('light', 'left-nav-active-tab-border-color'))
 *   content-secondary       → #777E8B  (was rd-theme('light', 'left-nav-section-title-color'))
 *   content-primary         → #1D232F  (was rd-theme('light', 'left-nav-item-color'))
 *   content-brand           → #2770EF  (was rd-theme('light', 'left-nav-item-selection-color'))
 *   background-ghost-highlight → rgba(#71A1F4, 0.12) (was left-nav-item-selection-background)
 */
const NAV_VAR_TO_SYSTEM_TOKEN: ReadonlyArray<{
    navVar: string;
    sysToken: string;
}> = [
    { navVar: '--left-nav-background', sysToken: 'background-base' },
    {
        navVar: '--left-nav-active-tab-background',
        sysToken: 'background-on-base',
    },
    {
        navVar: '--left-nav-active-tab-border-color',
        sysToken: 'border-subtle',
    },
    {
        navVar: '--left-nav-section-title-color',
        sysToken: 'content-secondary',
    },
    { navVar: '--left-nav-item-color', sysToken: 'content-primary' },
    { navVar: '--left-nav-item-selection-color', sysToken: 'content-brand' },
    {
        navVar: '--left-nav-item-selection-background',
        sysToken: 'background-ghost-highlight',
    },
];

/**
 * Top-nav variables owned by this bridge. `token` is the generated-surface
 * reference, `literal` the prepareTopNavInternalVariables() slot used when the
 * admin ticked "apply as top nav color". Both modes stay in one table so
 * deactivate() tears down everything activate() can set.
 */
const TOP_NAV_VARS: ReadonlyArray<{
    navVar: string;
    token: string | null;
    literal: keyof TopNavInternalVariablesProps | null;
}> = [
    // Top nav background: the inline style on the nav element reads
    // var(--ts-var-nav-background, ${navBackground}). Without this override,
    // the fallback is the legacy dark color from the config service.
    {
        navVar: '--ts-var-nav-background',
        token: 'var(--rd-sys-color-background-on-base)',
        literal: 'topNavBackground',
    },
    // Only meaningful in literal mode — the legacy top-nav SCSS reads it.
    {
        navVar: '--top-nav-background',
        token: null,
        literal: 'topNavBackground',
    },
    // App logo color: the AppLogo inline SVG defaults to white (#FFFFFF) for
    // legacy dark nav. Override with content-primary so it adapts to the active
    // radiant theme. Set on #root so all MFEs (nav, sql-view, etc.) inherit it.
    {
        navVar: '--ts-app-logo-color',
        token: 'var(--rd-sys-color-content-primary)',
        literal: 'topNavColor',
    },
    // Top nav text color (e.g. spotapp-export header title): defaults to
    // content-inverse (white on the legacy dark header). Override with
    // content-primary so the text adapts to the active radiant theme.
    {
        navVar: '--top-nav-text-color',
        token: 'var(--rd-sys-color-content-primary)',
        literal: 'topNavColor',
    },
    // Top nav icon color (help menu SVG etc): defaults to content-tertiary
    // in SCSS. Override with content-primary for radiant flow.
    {
        navVar: '--top-nav-color',
        token: 'var(--rd-sys-color-content-primary)',
        literal: 'topNavColor',
    },
    // Avatar button background: defaults to background-base-inverse (dark pill).
    // Override with button-background-secondary-default for radiant flow.
    {
        navVar: '--top-nav-action-item-background',
        token: 'var(--rd-comp-color-button-secondary-default)',
        literal: 'topNavActionItemBackground',
    },
    // Search placeholder — only the legacy derivation supplies one.
    {
        navVar: '--top-nav-action-item-placeholder-color',
        token: null,
        literal: 'topNavPlaceholderColor',
    },
    // Search box border: the SCSS fallback defaults to a legacy gray border.
    // Override with the design-system border token so it adapts to the active
    // radiant theme; the nav-theme-bridge removes it on deactivation.
    {
        navVar: '--ts-var-nav-action-item-border-color',
        token: 'var(--rd-sys-color-border-bold)',
        literal: 'topNavBorderColor',
    },
    // The legacy twin of the var above. setTopNavInternalVariables() can set
    // it on the first render, before radiantNavMode resolves, so the bridge
    // has to own it in both modes or a stale hex outlives the mode change.
    {
        navVar: '--top-nav-action-item-border-color',
        token: null,
        literal: 'topNavBorderColor',
    },
    {
        navVar: '--ts-var-nav-action-item-border-secondary-color',
        token: 'var(--rd-sys-color-border-bold)',
        literal: 'topNavBorderFocusColor',
    },
    // --top-nav-action-item-border-secondary-color is set by the legacy
    // setTopNavInternalVariables() on initial render (when radiantNavMode is
    // still null). Without this override, the stale hardcoded hex value lingers
    // and takes precedence over the --left-nav-active-tab-border-color fallback,
    // so the border color never adapts when switching to dark mode.
    // Uses border-subtle so the top nav border-bottom adapts to the active radiant theme.
    {
        navVar: '--top-nav-action-item-border-secondary-color',
        token: 'var(--rd-sys-color-border-subtle)',
        literal: 'topNavBorderFocusColor',
    },
    // ThoughtSpot logo <img> filter — legacy derivation only.
    {
        navVar: '--top-nav-ts-logo-filter',
        token: null,
        literal: 'topNavTsLogoFilter',
    },
];

export interface NavThemeBridgeOptions {
    /**
     * Brand hex to paint the top nav with literally ("apply as top nav
     * color"). Null/omitted keeps the top nav on the generated radiant
     * surface. An unparseable value is ignored rather than thrown, so a bad
     * persisted color degrades to the derived nav instead of breaking boot.
     */
    navBaseColor?: string | null;
}

/**
 * Activate the nav theme bridge.
 *
 * Sets each --left-nav-* variable on #root as a var(--rd-sys-color-*)
 * reference. Since inline styles beat stylesheet rules, these override
 * the SCSS-compiled rd-theme() values from [data-theme-type] selectors.
 *
 * The resolved color depends on the active system token values:
 *   - Light preset: default SCSS tokens (light palette)
 *   - Dark preset:  [data-theme='dark'] CSS rule overrides tokens
 *   - Brand:        applyTheme() injects custom token values on :root
 *
 * The left nav always follows the tokens. Only the top nav switches to the
 * literal hex, matching what appPanelColor has always painted.
 */
export function activateNavThemeBridge(
    options: NavThemeBridgeOptions = {},
): void {
    const rootEl = document.getElementById('root');
    if (!rootEl) return;

    const navBaseColor = options.navBaseColor
        ? normalizeBrandColor(options.navBaseColor)
        : null;
    const navLiteral = navBaseColor
        ? prepareTopNavInternalVariables(navBaseColor)
        : null;

    // Signal to CSS that radiant nav is active. Used by scoped selectors
    // (e.g. #root[data-radiant-nav] .navigationHubTab) to conditionally
    // apply icon fill overrides without breaking legacy flows.
    rootEl.setAttribute('data-radiant-nav', '');

    // Bridge left-nav CSS vars → system token references
    NAV_VAR_TO_SYSTEM_TOKEN.forEach(({ navVar, sysToken }) => {
        rootEl.style.setProperty(navVar, `var(--rd-sys-color-${sysToken})`);
    });

    // Left-nav tab icon colors: selected = content-primary, unselected = content-secondary.
    // SCSS uses no-fallback var() so legacy IconColor classes win when these are absent.
    rootEl.style.setProperty(
        '--ts-var-left-nav-tab-icon-active-color',
        'var(--rd-sys-color-content-primary)',
    );
    rootEl.style.setProperty(
        '--ts-var-left-nav-tab-icon-inactive-color',
        'var(--rd-sys-color-content-secondary)',
    );

    // Top-nav vars: literal brand hex when opted in, generated tokens otherwise.
    TOP_NAV_VARS.forEach(({ navVar, token, literal }) => {
        const value =
            navLiteral && literal ? navLiteral[literal] : token ?? null;
        if (value) {
            rootEl.style.setProperty(navVar, value);
        } else {
            // A var with no value in this mode (e.g. the logo filter outside
            // literal mode) must be cleared, not left over from the other one.
            rootEl.style.removeProperty(navVar);
        }
    });
}

/**
 * Deactivate the nav theme bridge — revert to SCSS-compiled defaults.
 *
 * Removes all inline CSS variable overrides from #root, letting the
 * SCSS [data-theme-type] selectors control nav colors again.
 */
export function deactivateNavThemeBridge(): void {
    const rootEl = document.getElementById('root');
    if (!rootEl) return;

    rootEl.removeAttribute('data-radiant-nav');

    NAV_VAR_TO_SYSTEM_TOKEN.forEach(({ navVar }) => {
        rootEl.style.removeProperty(navVar);
    });

    TOP_NAV_VARS.forEach(({ navVar }) => {
        rootEl.style.removeProperty(navVar);
    });

    rootEl.style.removeProperty('--ts-var-left-nav-tab-icon-active-color');
    rootEl.style.removeProperty('--ts-var-left-nav-tab-icon-inactive-color');
}
